import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  Optional,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormArray,
  UntypedFormGroup,
  UntypedFormBuilder,
  AbstractControl,
  Validators,
  ValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';

import {
  CustomFieldApiService, SnackBarConfig
} from '../../../shared';
import { trackByValue, trackByControlValue } from '../../../shared/util';
import {
  ICustomField,
  ECustomFieldType,
  CUSTOM_FIELD_OBJECTS
} from '../../interfaces/custom-field.interface';

@Component({
  selector: 'lum-custom-field-form',
  templateUrl: './custom-field-form.component.html',
  styleUrls: ['./custom-field-form.component.scss'],
})
export class CustomFieldFormComponent implements OnInit, OnDestroy {
  customFieldId :number = 0;

  customField :ICustomField;

  submittingForm :boolean = false;
  loading :boolean = false;

  form :UntypedFormGroup;

  eCustomFieldType = ECustomFieldType;

  trackByValue = trackByValue;
  trackByControlValue = trackByControlValue;

  private onDestroy$ = new Subject();

  // Use the shared constant for available objects
  availableCustomFieldObjects = CUSTOM_FIELD_OBJECTS;

  constructor(
    private customFieldApiService :CustomFieldApiService,
    private route :ActivatedRoute,
    private router :Router,
    private cd :ChangeDetectorRef,
    private fb :UntypedFormBuilder,
    private snackBar :MatSnackBar,
    @Optional() public dialogRef :MatDialogRef<CustomFieldFormComponent>,
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.getRouterParams().subscribe();
    this.watchTypeValueChanges();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  createDropdownOption() :UntypedFormControl {
    return new UntypedFormControl('', Validators.required);
  }

  addDropdownOption() :void {
    const options = this.form.get('options') as UntypedFormArray;
    options.push(this.createDropdownOption());
    options.updateValueAndValidity();
    this.cd.detectChanges();
  }

  removeDropdownOption(index :number) :void {
    const options = this.form.get('options') as UntypedFormArray;
    options.removeAt(index);
    options.updateValueAndValidity();
    this.cd.detectChanges();
  }

  markFormArrayAsTouched(formArray :UntypedFormArray) :void {
    formArray.controls.forEach((control) => {
      if (control instanceof UntypedFormControl) {
        control.markAsTouched();
      } else if (control instanceof UntypedFormGroup) {
        this.markFormGroupAsTouched(control);
      } else if (control instanceof UntypedFormArray) {
        this.markFormArrayAsTouched(control);
      }
    });
  }

  markFormGroupAsTouched(formGroup :UntypedFormGroup) :void {
    Object.values(formGroup.controls).forEach((control) => {
      if (control instanceof UntypedFormControl) {
        control.markAsTouched();
      } else if (control instanceof UntypedFormGroup) {
        this.markFormGroupAsTouched(control);
      } else if (control instanceof UntypedFormArray) {
        this.markFormArrayAsTouched(control);
      }
    });
  }

  onSubmit() :void {
    this.form.markAsTouched();
    this.form.markAsDirty();

    const optionsControl = this.form.get('options');
    if (optionsControl && optionsControl instanceof UntypedFormArray) {
      this.markFormArrayAsTouched(optionsControl);
    }
    // Mark customFieldObjectTypes as touched
    const customFieldObjectTypesControl = this.form.get('customFieldObjectTypes');
    if (customFieldObjectTypesControl && customFieldObjectTypesControl instanceof UntypedFormArray) {
      this.markFormArrayAsTouched(customFieldObjectTypesControl);
    }

    if (this.form.invalid) {
      if (
        this.form.value.type === ECustomFieldType.DROPDOWN &&
        this.form.value.options.length === 0
      ) {
        this.snackBar.open('At least one option is required in the field', 'Fail', {
          duration: SnackBarConfig.DEFAULT.duration,
        });
      }
      if (this.form.errors?.atLeastOneObject) {
        this.snackBar.open('Select at least one object for this custom field', 'Fail', {
          duration: SnackBarConfig.DEFAULT.duration,
        });
      }
      return;
    }

    this.submittingForm = true;

    try {
      if (!this.customFieldId) {
        this.customFieldApiService
          .create({
            label: this.form.value.label,
            type: this.form.value.type,
            options: this.form.value.options,
            customFieldObjects: this.selectedObjectTypes,
          })
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (response) => {
              this.submittingForm = false;
              this.snackBar.open('Custom Field successfully added.', 'Success', {
                duration: SnackBarConfig.DEFAULT.duration,
              });

              if (this.isDialog) {
                this.dialogRef.close(response);
              } else {
                this.router.navigate(['custom-fields']);
                this.cd.detectChanges();
              }
            },
            error: (response) => {
              this.submittingForm = false;
              this.snackBar.open(response.error, 'Fail', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
              this.cd.detectChanges();
            },
          });
      } else {
        this.customFieldApiService
          .update(this.customFieldId, {
            label: this.form.value.label,
            type: this.form.value.type,
            options: this.form.value.options,
            customFieldObjects: this.selectedObjectTypes,
          })
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (response) => {
              this.submittingForm = false;
              this.snackBar.open('Custom Field successfully updated.', 'Success', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
              this.router.navigate(['custom-fields']);
              this.cd.detectChanges();
            },
            error: (response) => {
              this.submittingForm = false;
              this.snackBar.open(response.error, 'Fail', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
              this.cd.detectChanges();
            },
          });
      }
    } catch (error :any) {
      this.submittingForm = false;
      this.snackBar.open(error, 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });
      this.cd.detectChanges();
    }
  }

  private watchTypeValueChanges() :void {
    this.form.get('type')?.valueChanges.subscribe((value) => {
      const optionsControl = this.form.get('options') as UntypedFormArray;

      if (value === ECustomFieldType.DROPDOWN) {
        this.form.addControl(
          'options',
          new UntypedFormArray([this.createDropdownOption()]),
        );
      } else {
        optionsControl.clearValidators();
        optionsControl.updateValueAndValidity();
        this.form.removeControl('options');
      }
      this.cd.detectChanges();
    });
  }

  private initForm() :void {
    this.form = this.fb.group(
      {
        label: [null, [Validators.required]],
        type: [ECustomFieldType.STRING, [Validators.required]],
        options: this.fb.array([]),
        customFieldObjectTypes: this.fb.array(this.availableCustomFieldObjects.map(() => false)),
      },
      { validators: this.dropdownOptionsValidator() },
    );
  }

  private dropdownOptionsValidator() :ValidatorFn {
    return (control :AbstractControl) :ValidationErrors | null => {
      const group = control as UntypedFormGroup;
      const type = group.controls['type'].value;
      const options = group.controls['options'] as UntypedFormArray;

      if (type === ECustomFieldType.DROPDOWN && options.length === 0) {
        return { atLeastOneOptionRequired: true };
      }

      return null;
    };
  }

  private setFormValue() :void {
    if (this.customField && this.customField.id) {
      this.form?.get('label')?.setValue(this.customField.label);
      this.form?.get('type')?.setValue(this.customField.type);

      if (
        this.customField.type === ECustomFieldType.DROPDOWN &&
        this.customField.options
      ) {
        const optionsArray = this.optionsFormArray;
        this.customField.options.forEach((option) => {
          optionsArray.push(new UntypedFormControl(option, Validators.required));
        });
      }

      // Set checkboxes for customFieldObjectTypes when editing
      if (this.customField.customFieldObjectTypes) {
        const objectsFormArray = this.form.get('customFieldObjectTypes') as UntypedFormArray;
        objectsFormArray.controls.forEach((control, idx) => {
          const objectType = this.availableCustomFieldObjects[idx].value;
          control.setValue(this.customField.customFieldObjectTypes.includes(objectType));
        });
      }

      this.cd.detectChanges();
    }
  }

  private loadData(id :number) :void {
    this.loading = true;
    this.customFieldApiService
      .getById(id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.customField = response;
          this.setFormValue();
        },
        error: (response) => {
          this.loading = false;
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.cd.detectChanges();
        },
      });
  }

  private getRouterParams() :Observable<Params> {
    return this.route.params.pipe(
      tap((params :Params) => {
        const { customFieldId } = params;
        if (customFieldId) {
          this.customFieldId = customFieldId;
          this.loadData(this.customFieldId);
        }
        this.cd.detectChanges();
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get customFieldTypes() :ECustomFieldType[] {
    return [
      ECustomFieldType.STRING,
      ECustomFieldType.NUMBER,
      ECustomFieldType.DATE,
      ECustomFieldType.DROPDOWN,
    ];
  }

  get optionsControls() :AbstractControl[] {
    const control = this.form.get('options');

    return control instanceof UntypedFormArray ? control.controls : [];
  }

  get optionsFormArray() :UntypedFormArray {
    return this.form.get('options') as UntypedFormArray;
  }

  get customFieldObjectTypesFormArray(): UntypedFormArray {
    return this.form.get('customFieldObjectTypes') as UntypedFormArray;
  }

  get selectedObjectTypes(): string[] {
    return this.availableCustomFieldObjects
      .filter((_, index) => this.form.value.customFieldObjectTypes[index])
      .map(obj => obj.value);
  }

  get isDialog() :boolean {
    return this.dialogRef?.componentInstance === this;
  }
}
